import * as actions from '../actions'
import { ArticleIndex } from '../ArticleIndex'
import { produce } from 'immer'
import { Action } from '@reduxjs/toolkit'
import { AppMode } from '../constants'
import { WritableDraft } from 'immer/dist/internal';
import { ImportOrderItem } from '../actions';
import { Assortment, DEFAULT_FILTERS, Filters, matchesFilters, Unit } from '../services/assortment.service';

export interface ImagePosition {
	x: number
	y: number
	z: number
}

export interface ImageConfig {
	fileName: string
	path: string
	filter: string
	img_pos: ImagePosition
	show: boolean
	index: ArticleIndex
}

export interface ImageData {
	image: HTMLCanvasElement | HTMLImageElement
	offsetX: number
	offsetY: number
	origWidth: number
	origHeight: number
}

export interface Color {
	name: string
	color: string
	font_color: string
	selection_color: string
	selectionBrighten: string
	bg_color: string
	fileName: string
	path: string
}

export interface OrderItem {
	systemData: Filters
	symKar: string
	count: number
}

export interface OrderValue {
	unit: Unit
	count: number
}

export interface AppState {
	loading: boolean
	msg: string
	selected: number
	abortFn: () => void | null
	navType: string
	modalSrc: string | null
	showPartImage: boolean
	config: any
	lang: string | null
	assortment: Assortment
	filteredAssortment: Assortment
	filters: Filters
	isFiltering: boolean
	images: Map<string, ImageData | null>
	listImages: Map<string, URL | null>
	sliderImagesLinks: string[],
	imagesLoading: boolean,
	order: {[key: number]: OrderValue[]}
}

export interface AppAction extends Action<string> {
	payload: any
	meta?: any
}

const loadConfigData = (configJSON: string, state: WritableDraft<AppState>) => {
	try {
		const config = JSON.parse(configJSON)
		state.config = config
		state.lang = config.languages[0]
		state.sliderImagesLinks = state.config.sliderImgsNames
		.map(function(str) {
			return `${state.config.sliderImgsPath}${str}`
		});
	} catch (ex) {
		state.msg = "Błąd przy ładowaniu konfiguracji: Niewłaściwy format."
	}
}

const loadSystemsData = (data: any, state: WritableDraft<AppState>) => {
	try {
		state.assortment = data;
		state.filteredAssortment = data;
		state.loading = false;
	} catch (ex) {
		state.msg = "Błąd przy ładowaniu asortymentu: Niewłaściwy format."
	}
};

const importPreviousOrder = (state: WritableDraft<AppState>, oldOrder: ImportOrderItem[]) => {
	if (state.config?.newOrderApplication) {
		oldOrder.forEach(item => {
			for (const prd of state.assortment.products) {
				if (matchesFilters(prd, item.systemData) && prd.symKar === item.product_id) {
					if (!state.order[prd.id]) state.order[prd.id] = [];
					const unitIndex = state.order[prd.id].findIndex(o => o.unit.converter === item.product_unit.converter)
					if(unitIndex >= 0) state.order[prd.id][unitIndex].count = item.product_qty
					else state.order[prd.id].push({ count: item.product_qty, unit: item.product_unit } as OrderValue);
					break;
				}
			}
		});
	} else {
		oldOrder.forEach(item => {
			for (const prd of state.assortment.products) {
				if (prd.symKar === item.product_id) {
					if (!state.order[prd.id]) state.order[prd.id] = [];
					const unitIndex = state.order[prd.id].findIndex(o => o.unit.converter === item.product_unit.converter)
					if(unitIndex >= 0) state.order[prd.id][unitIndex].count = item.product_qty
					else state.order[prd.id].push({ count: item.product_qty, unit: item.product_unit } as OrderValue);
					break;
				}
			}
		});
	}
	for (const prd of state.assortment.products) {
		if (state.order[prd.id]) state.order[prd.id].sort((a, b) => a.unit.converter - b.unit.converter)
	}
}

export default (oldState: AppState, action: AppAction): AppState => {
	if (oldState === undefined) {
		return {
			loading: true,
			msg: "",
			selected: -1,
			config: null,
			abortFn: null,
			navType: "click",
			modalSrc: null,
			showPartImage: false,
			lang: null,
			assortment: {
				products: [],
				gutterShapes: [],
				materials: [],
				grilles: [],
				tubeShapes: [],
				sizes: [],
				colors: {}
			},
			filteredAssortment: {
				products: [],
				gutterShapes: [],
				materials: [],
				grilles: [],
				tubeShapes: [],
				sizes: [],
				colors: {},
			},
			filters: {
				gutterShape: null,
				material: null,
				grille: null,
				tubeShape: null,
				size: null,
				colorName: null
			},
			isFiltering: false,
			images: new Map(),
			listImages: new Map(),
			sliderImagesLinks: [],
			imagesLoading: false,
			order: {}
		}
	}

	return produce(oldState, state => {
		switch (action.type) {
			case actions.JSON_LOAD_SUCCESS:
				if (action.payload.type === actions.SYSTEMS_JSON) {
					loadSystemsData(action.payload.json, state);
				} else if (action.payload.type === actions.CONFIG_JSON) {
					loadConfigData(action.payload.json, state)
				}
				break
			case actions.SET_MESSAGE:
				state.msg = action.payload.msg
				break
			case actions.IMAGE_ERROR:
				if (state.config.appMode !== AppMode.Editor) break
				state.msg = action.payload.msg
				break
			case actions.setAbortFn.type:
				state.abortFn = action.payload
				break
			case actions.SELECT_ARTICLE:
				state.selected = action.payload
				break
			case actions.TOGGLE_NAV:
				if (state.navType === 'click') {
					state.navType = 'mouseenter'
				} else if (state.navType === 'mouseenter') {
					state.navType = 'click'
				}
				break
			case actions.MODAL:
				state.modalSrc = action.payload.src
				break
			case actions.TOGGLE_PART_IMAGE:
				state.showPartImage = !state.showPartImage
				break
			case actions.LANG_CHANGE:
				state.lang = action.payload.lang
				break
			case actions.LOAD_OLD_ORDER:
				importPreviousOrder(state, action.payload.oldOrder)
				break
			case actions.RESET_ORDER:
				state.assortment.products.forEach(prd => {
					if (matchesFilters(prd, state.filters)) {
						state.order[prd.id] = [];
					}
				})
				break
			case actions.SET_FILTER:
				state.filters[action.payload.name] = action.payload.value
				break
			case actions.SET_IS_FILTERING:
				state.isFiltering = action.payload
				break
			case actions.CLEAR_FILTERS:
				state.filters = DEFAULT_FILTERS
				break
			case actions.SET_FILTERED_ASSORTMENT:
				state.filteredAssortment = action.payload
				break
			case actions.SET_PRODUCTS_TO_SHOW:
				state.filteredAssortment.products = action.payload
				break
			case actions.loadAndProcessImage.fulfilled.type:
				state.images.set(action.payload.url, action.payload.imgData)
				break
			case actions.loadListImage.fulfilled.type:
				state.listImages.set(action.payload.reqUrl, action.payload.objUrl)
				break
			case actions.SET_IMAGES_LOADING:
				state.imagesLoading = action.payload
				break
			case actions.UPDATE_IMG_POS:
				let movedPrd = state.assortment.products.find(prd => prd.id === action.payload.id)
				movedPrd.posX = action.payload.pos.x
				movedPrd.posY = action.payload.pos.y
				movedPrd.posZ = action.payload.pos.z

				let movedPrdFiltered = state.filteredAssortment.products.find(prd => prd.id === action.payload.id)
				movedPrdFiltered.posX = action.payload.pos.x
				movedPrdFiltered.posY = action.payload.pos.y
				movedPrdFiltered.posZ = action.payload.pos.z
				break
			case actions.EDIT_ORDER:
				if (!state.order[action.payload.id]) state.order[action.payload.id] = [];
				const orderIndex = state.order[action.payload.id].findIndex(o => o.unit.converter === action.payload.unit.converter);
				if (orderIndex >= 0) {
					state.order[action.payload.id][orderIndex].count = action.payload.count;
				} else {
					state.order[action.payload.id].push({count: action.payload.count, unit: action.payload.unit} as OrderValue)
				}
				state.order[action.payload.id].sort((a, b) => a.unit.converter - b.unit.converter)
				break
			case actions.SET_APP_MODE:
				state.config.appMode = action.payload
				break
			default:
				break
		}
	})
}
